/**
 * tokenRefresh — keeps the facilitator signed in past the 60-minute token.
 *
 * Scoring 2026-09-30: every facilitator sign-in (password, email code)
 * also returns a `refresh_token`. POST /api/scheduler/facilitators/refresh
 * swaps it for a new access + refresh pair.
 *
 * Each refresh token works ONCE, and reusing one cancels them all. So:
 *  - one renewal at a time per tab (a shared in-flight promise), and
 *    across tabs (a Web Lock where the browser has it);
 *  - a request that failed with an older token than the one now stored
 *    just retries with the stored one instead of renewing again.
 *
 * Renewal happens quietly ~10 minutes before the token runs out, and on a
 * 401 as a fallback. If renewal fails, the caller signs out as before
 * (notifyAuthExpired). Tokens are never logged.
 */
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '@/lib/apiBase';
import { notifyAuthExpired, requestHadToken } from '@/lib/authSession';

export const ACCESS_TOKEN_KEY = 'fp-access-token-v1';
export const REFRESH_TOKEN_KEY = 'fp-refresh-token-v1';

const RENEW_BEFORE_EXPIRY_MS = 10 * 60_000;
const LOCK_NAME = 'fp-token-renewal';

function read(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

/** Store a fresh sign-in. The refresh token may be missing (e.g. sign-up). */
export function saveTokens(access: string, refresh?: string | null): void {
  try {
    localStorage.setItem(ACCESS_TOKEN_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
    else localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch { /* private mode or storage disabled */ }
  scheduleQuietRenewal();
}

export function clearTokens(): void {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch { /* noop */ }
  if (quietTimer) { clearTimeout(quietTimer); quietTimer = null; }
}

// ─── Renewal ────────────────────────────────────────────────────────────────

let inFlight: Promise<string | null> | null = null;

/**
 * Get a working access token. `staleAccess` is the token that just failed
 * (or is about to run out); if a newer one is already stored, that one is
 * returned without renewing. Resolves null when renewal is not possible.
 */
export function renewAccessToken(staleAccess: string | null): Promise<string | null> {
  if (!inFlight) {
    const run = () => renewOnce(staleAccess);
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    // The lock resolves with run()'s own result; its typing nests the promise.
    const locked = locks?.request
      ? (locks.request(LOCK_NAME, run) as unknown as Promise<string | null>)
      : run();
    inFlight = locked
      .finally(() => { inFlight = null; });
  }
  return inFlight;
}

async function renewOnce(staleAccess: string | null): Promise<string | null> {
  const current = read(ACCESS_TOKEN_KEY);
  // Another request or tab already renewed: use what it stored.
  if (current && current !== staleAccess) return current;

  const refresh = read(REFRESH_TOKEN_KEY);
  if (!refresh) return null;

  let resp: Response;
  try {
    resp = await fetch(`${API_BASE_URL}/api/scheduler/facilitators/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refresh }),
    });
  } catch {
    console.warn('[FP auth] renewal failed: no connection');
    return null;
  }

  if (!resp.ok) {
    // 401/400: the refresh token is spent, expired, or the account is closed
    // (facilitator_inactive / facilitator_not_found). It will never work again.
    // 429/5xx: keep it; a later try may succeed.
    if (resp.status < 500 && resp.status !== 429) {
      try { localStorage.removeItem(REFRESH_TOKEN_KEY); } catch { /* noop */ }
    }
    let reason = '';
    try {
      const body = await resp.json();
      reason = body?.detail?.reason ?? body?.reason ?? '';
    } catch { /* not JSON */ }
    console.warn('[FP auth] renewal refused', resp.status, reason);
    return null;
  }

  const body = await resp.json() as { access_token?: string; refresh_token?: string };
  if (!body.access_token) return null;
  saveTokens(body.access_token, body.refresh_token);
  return body.access_token;
}

// ─── Quiet renewal before expiry ────────────────────────────────────────────

let quietTimer: ReturnType<typeof setTimeout> | null = null;

/** The token's expiry (from its payload) in epoch milliseconds, or null. */
function expiryMs(token: string): number | null {
  try {
    const part = token.split('.')[1];
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = (JSON.parse(json) as { exp?: number }).exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch { return null; }
}

/** (Re)arm the timer that renews ~10 minutes before the stored token expires. */
export function scheduleQuietRenewal(): void {
  if (quietTimer) { clearTimeout(quietTimer); quietTimer = null; }
  const access = read(ACCESS_TOKEN_KEY);
  if (!access || !read(REFRESH_TOKEN_KEY)) return;
  const exp = expiryMs(access);
  if (exp == null) return;
  const delay = Math.max(0, exp - RENEW_BEFORE_EXPIRY_MS - Date.now());
  quietTimer = setTimeout(() => {
    quietTimer = null;
    void renewAccessToken(access);
  }, delay);
}

// A laptop lid or a background tab can hold a timer back; re-check on return.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') scheduleQuietRenewal();
  });
}

// ─── 401 handling for the facilitator's axios clients ───────────────────────

type RenewableConfig = InternalAxiosRequestConfig & { _fpRenewed?: boolean };

function bearerOf(headers: unknown): string | null {
  const h = headers as Record<string, unknown> | undefined;
  const auth = h?.Authorization ?? h?.authorization;
  return typeof auth === 'string' && auth.startsWith('Bearer ') ? auth.slice(7) : null;
}

/**
 * On a 401 for a signed-in request: renew once and retry. Only if that
 * fails does the portal sign out (notifyAuthExpired).
 */
export async function renewAndRetry(instance: AxiosInstance, error: AxiosError): Promise<unknown> {
  const config = error.config as RenewableConfig | undefined;
  if (!config || !requestHadToken(config.headers)) throw error;
  if (config._fpRenewed) {
    notifyAuthExpired();
    throw error;
  }
  const fresh = await renewAccessToken(bearerOf(config.headers));
  if (!fresh) {
    notifyAuthExpired();
    throw error;
  }
  config._fpRenewed = true;
  config.headers.Authorization = `Bearer ${fresh}`;
  return instance(config);
}
